import { Visibility, VisibilityOff } from '@mui/icons-material';
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

// Player form type definition
export interface PlayerFormData {
  playerCode: string;
  apiKey: string;
  apiSecret: string;
  name: string;
}

// Validation error type definition
export interface PlayerFormErrors {
  playerCode?: string;
  apiKey?: string;
  apiSecret?: string;
  name?: string;
}

interface PlayerFormProps {
  initialData: PlayerFormData;
  onSubmit: (formData: PlayerFormData) => Promise<void>;
  submitLabel: string;
  loading: boolean;
  showId?: boolean;
  playerId?: number;
}

export function PlayerForm({
  initialData,
  onSubmit,
  submitLabel,
  loading,
  showId,
  playerId,
}: PlayerFormProps) {
  const { t } = useTranslation('players');
  const [formData, setFormData] = useState<PlayerFormData>(initialData);
  const [errors, setErrors] = useState<PlayerFormErrors>({});
  const [showApiSecret, setShowApiSecret] = useState(false);

  // Update form data when initial data changes
  useEffect(() => {
    setFormData(initialData);
  }, [initialData]);

  /**
   * Form input change handler
   */
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Clear errors on input
    if (errors[name as keyof PlayerFormErrors]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name as keyof PlayerFormErrors];
        return newErrors;
      });
    }
  };

  /**
   * Toggle API secret visibility
   */
  const handleToggleShowApiSecret = () => {
    setShowApiSecret((prev) => !prev);
  };

  /**
   * Form validation
   */
  const validateForm = (): boolean => {
    const newErrors: PlayerFormErrors = {};

    if (!formData.playerCode) {
      newErrors.playerCode = t('form.errors.playerCodeRequired');
    }

    if (!formData.apiKey) {
      newErrors.apiKey = t('form.errors.apiKeyRequired');
    }

    if (!formData.apiSecret) {
      newErrors.apiSecret = t('form.errors.apiSecretRequired');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * Form submission handler
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    await onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit}>
      <Grid container spacing={3}>
        {showId && playerId !== undefined && (
          <Grid component="div" sx={{ width: { xs: '100%', sm: '50%', md: '33.33%' } }}>
            <TextField
              fullWidth
              label={t('form.fields.playerId')}
              value={playerId}
              disabled
            />
          </Grid>
        )}
        <Grid component="div" sx={{ width: { xs: '100%', sm: '50%', md: '33.33%' } }}>
          <TextField
            fullWidth
            label={t('form.fields.playerCode')}
            name="playerCode"
            value={formData.playerCode}
            onChange={handleInputChange}
            error={!!errors.playerCode}
            helperText={errors.playerCode}
            disabled={loading}
            required
          />
        </Grid>
        <Grid component="div" sx={{ width: { xs: '100%', sm: '50%', md: '33.33%' } }}>
          <TextField
            fullWidth
            label={t('form.fields.playerName')}
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            error={!!errors.name}
            helperText={errors.name}
            disabled={loading}
          />
        </Grid>
        <Grid component="div" sx={{ width: { xs: '100%', sm: '50%', md: '33.33%' } }}>
          <TextField
            fullWidth
            label={t('form.fields.apiKey')}
            name="apiKey"
            value={formData.apiKey}
            onChange={handleInputChange}
            error={!!errors.apiKey}
            helperText={errors.apiKey}
            disabled={loading}
            required
          />
        </Grid>
        <Grid component="div" sx={{ width: { xs: '100%', sm: '50%', md: '33.33%' } }}>
          <TextField
            fullWidth
            label={t('form.fields.apiSecret')}
            name="apiSecret"
            type={showApiSecret ? 'text' : 'password'}
            value={formData.apiSecret}
            onChange={handleInputChange}
            error={!!errors.apiSecret}
            helperText={errors.apiSecret}
            disabled={loading}
            required
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label={t('form.toggleApiSecretVisibility')}
                    onClick={handleToggleShowApiSecret}
                    edge="end"
                  >
                    {showApiSecret ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Grid>
      </Grid>

      <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          color="primary"
          type="submit"
          disabled={loading}
          sx={{ minWidth: 120 }}
        >
          {loading ? <CircularProgress size={24} /> : submitLabel}
        </Button>
      </Box>
    </form>
  );
}

export default PlayerForm;
